import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const IncomeVsCostsChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                return {
                    date,
                    revenue: metrics?.revenue ?? 0,
                    adSpend: metrics?.adSpend ?? 0,
                    discounts: metrics?.discounts ?? 0,
                    returns: metrics?.returns ?? 0,
                    transactionFees: metrics?.transactionFees ?? 0,
                    shippingRevenue: metrics?.shippingRevenue ?? 0,
                };
            });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                <defs>
                    <linearGradient id="adSpendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.category.advertising} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={CHART_COLORS.category.advertising} stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="discountsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.category.discounts} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={CHART_COLORS.category.discounts} stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="returnsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.category.returns} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={CHART_COLORS.category.returns} stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="feesGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.category.fees} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={CHART_COLORS.category.fees} stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="shippingGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.category.shipping} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={CHART_COLORS.category.shipping} stopOpacity={0.1} />
                    </linearGradient>
                </defs>
                <CartesianGrid
                    vertical={false}
                    stroke={CHART_STYLES.grid.stroke}
                    strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                    strokeWidth={CHART_STYLES.grid.strokeWidth}
                    opacity={CHART_STYLES.grid.opacity}
                />
                <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                    {...CHART_STYLES.axis}
                />
                <YAxis
                    tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                    {...CHART_STYLES.axis}
                />
                <Tooltip
                    contentStyle={CHART_STYLES.tooltip.contentStyle}
                    cursor={CHART_STYLES.tooltip.cursor}
                    itemStyle={CHART_STYLES.tooltip.itemStyle}
                    formatter={(value: number, name: string) => [formatCurrency(value, currencySymbol), name]}
                    labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Area
                    type="monotone"
                    dataKey="adSpend"
                    name={t('charts.adSpend', 'Ad Spend')}
                    stackId="costs"
                    stroke={CHART_COLORS.category.advertising}
                    fill="url(#adSpendGrad)"
                />
                <Area
                    type="monotone"
                    dataKey="discounts"
                    name={t('charts.discounts', 'Discounts')}
                    stackId="costs"
                    stroke={CHART_COLORS.category.discounts}
                    fill="url(#discountsGrad)"
                />
                <Area
                    type="monotone"
                    dataKey="returns"
                    name={t('charts.returns', 'Returns')}
                    stackId="costs"
                    stroke={CHART_COLORS.category.returns}
                    fill="url(#returnsGrad)"
                />
                <Area
                    type="monotone"
                    dataKey="transactionFees"
                    name={t('charts.fees', 'Fees')}
                    stackId="costs"
                    stroke={CHART_COLORS.category.fees}
                    fill="url(#feesGrad)"
                />
                <Area
                    type="monotone"
                    dataKey="shippingRevenue"
                    name={t('charts.shipping', 'Shipping')}
                    stackId="costs"
                    stroke={CHART_COLORS.category.shipping}
                    fill="url(#shippingGrad)"
                />
                <Line
                    type="monotone"
                    dataKey="revenue"
                    name={t('charts.revenue', 'Revenue')}
                    stroke={CHART_COLORS.revenue}
                    strokeWidth={CHART_STYLES.line.strokeWidth}
                    dot={false}
                    activeDot={CHART_STYLES.line.activeDot}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
};
